import React from "react";

import { IconRenderer } from "./IconRenderer";

export interface PhilosophyPoint {
  text?: string;
  iconName?: string;
}

export interface TreatmentPhilosophyProps {
  heading?: string;
  description?: string;
  bulletPoints?: string[];
  highlightLine?: string;
  pointsWithIcons?: PhilosophyPoint[]; // optional override with icons
}

function renderIcon(iconName?: string) {
  return (
    <IconRenderer
      name={iconName || "shield-check"}
      className="size-8 text-violet-950"
    />
  );
}

/**
 * Treatment Philosophy Section - Key Differentiator
 * Shows Trichos' medical, case-by-case approach
 * Creative design with modern gradients and enhanced visuals
 */
export default function TreatmentPhilosophy({
  heading = "A Medical, Case-by-Case Approach to Hair Restoration",
  description = "At Trichos, hair restoration is approached as a medical decision, not a cosmetic shortcut.",
  bulletPoints,
  highlightLine = "Not every individual requires surgery — and we are clear about that.",
  pointsWithIcons,
}: TreatmentPhilosophyProps) {
  const defaultPoints = [
    "Consultation required before any recommendation",
    "Treatments advised only when medically appropriate",
    "Planning based on donor quality, scalp health & expectations",
    "Safety, ethics & long-term outcomes prioritised",
  ];

  const points =
    bulletPoints && bulletPoints.length > 0 ? bulletPoints : defaultPoints;
  const iconPoints =
    pointsWithIcons && pointsWithIcons.length > 0
      ? pointsWithIcons
      : [
          { iconName: "FaStethoscope", text: points[0] },
          { iconName: "FaClipboardCheck", text: points[1] },
          { iconName: "FaUserMd", text: points[2] },
          { iconName: "FaShieldAlt", text: points[3] },
        ];

  // Gradient colors for each card
  const cardGradients = [
    "linear-gradient(135deg, #E8F4F8 0%, #D1E7ED 100%)",
    "linear-gradient(135deg, #F0E8F5 0%, #E1D1EB 100%)",
    "linear-gradient(135deg, #E8F5E8 0%, #D1EDD1 100%)",
    "linear-gradient(135deg, #FFF5E8 0%, #FFEED1 100%)",
  ];

  return (
    <section
      className="py-16 md:py-24"
      style={{
        background:
          "linear-gradient(to bottom, #F8F9FA 0%, #E8ECF1 50%, #F8F9FA 100%)",
      }}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-5xl">
          {/* Header Section */}
          <div className="mb-12 text-center">
            <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">
              {heading}
            </h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-700 md:text-xl">
              {description}
            </p>
          </div>

          {/* Philosophy Points Grid */}
          <div className="mb-12 grid gap-6 md:grid-cols-2">
            {iconPoints.map((point, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl p-6 shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl md:p-8"
                style={{
                  background: cardGradients[index % cardGradients.length],
                }}
              >
                {/* Decorative corner element */}
                <div
                  className="absolute -right-8 -top-8 size-24 rounded-full opacity-10"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 70%)",
                  }}
                />
                <div className="relative flex items-start gap-5">
                  {/* Icon Container */}
                  <div className="flex-shrink-0">
                    <div className="rounded-xl bg-white p-3 shadow-md transition-transform group-hover:scale-110 group-hover:rotate-3">
                      {renderIcon(point.iconName)}
                    </div>
                  </div>
                  {/* Text Content */}
                  <p className="flex-1 text-left text-base font-medium leading-relaxed text-gray-800 md:text-lg">
                    {point.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Highlight Statement */}
          <div className="relative overflow-hidden rounded-3xl p-8 shadow-xl md:p-10">
            <div
              className="absolute inset-0 opacity-5"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.5) 0%, transparent 70%)",
              }}
            />
            <p className="relative text-center text-xl font-bold leading-relaxed text-gray-900 md:text-2xl">
              {highlightLine}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
